import React, { FC, useEffect, useMemo, useRef, useState } from 'react';

import {
	BUTTON_TYPES,
	EvSection,
	EvTextInput,
	OptionType,
	TOAST_STATUS
} from '@evinced-private/ui-common';

import { createDefaultRuleInUIFormat } from '../../helpers/UrlDiscoveryScopeHelper';
import { generateUUID } from '../../helpers/UUIDHelper';
import { IPropertyView } from '../../interfaces/PropertyViews';
import { IScopeRuleUIFormat } from '../../interfaces/ScopeRuleUIFormat';
import { useConfiguration } from '../../providers/configurationProvider/ConfigurationProvider';
import { useNotifications } from '../../providers/notificationsProvider/NotificationsConsumer';
import { INotificationsContext } from '../../providers/notificationsProvider/NotificationsContext';
import { IPropertyViewContext, usePropertyViewContext } from '../../providers/PropertyViewProvider';
import { SiteScannerUiToggles } from '../../services/LocalTogglesService';
import { logger } from '../../services/Logger';
import { SCOPE_TYPES } from '../../types/scope/ScopeTypes';
import { SiteScannerPopup } from '../common/site-scanner-popup/SiteScannerPopup';

import { ScopeHeader } from './scope/scope-header/ScopeHeader';
import { ScopeRulesList } from './scope/scope-rules-list/ScopeRulesList';
import {
	buildRulesList,
	createDefaultViewsRulesList,
	editExistingView,
	saveNewView
} from './EditViewButtonHelper';

import './EditViewButton.scss';

const ACTIONS_LIST: SCOPE_TYPES[] = [
	SCOPE_TYPES.URL,
	SCOPE_TYPES.ISSUE_TYPES,
	SCOPE_TYPES.ISSUE_SEVERITIES
];

interface IEditViewButtonProps {
	propertyId: string;
	innerText: string;
	type: BUTTON_TYPES;
	issueTypesList: OptionType[];
	issueSeveritiesList: OptionType[];
	view?: IPropertyView;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	accessibilityProps?: any;
}

export const EditViewButton: FC<IEditViewButtonProps> = ({
	propertyId,
	innerText,
	type,
	accessibilityProps,
	view = null,
	issueTypesList,
	issueSeveritiesList
}) => {
	const propertyViewContext: IPropertyViewContext = usePropertyViewContext();
	const notificationsContext: INotificationsContext = useNotifications();
	const { getToggle } = useConfiguration();

	const [viewName, setViewName] = useState<string>('');
	const [rulesList, setRulesList] = useState<IScopeRuleUIFormat[]>();

	const isSeverityFilterEnabled = getToggle(SiteScannerUiToggles.ENABLE_SEVERITY_VIEW_FILTER);

	const FILTERED_ACTIONS_LIST: SCOPE_TYPES[] = useMemo(() => {
		if (isSeverityFilterEnabled) {
			return ACTIONS_LIST;
		}
		return ACTIONS_LIST.filter((action) => action !== SCOPE_TYPES.ISSUE_SEVERITIES);
	}, [isSeverityFilterEnabled]);

	const defaultRulesList = useRef<IScopeRuleUIFormat[]>(null);
	const ARIA_LABEL_ID: string = useRef<string>(`view-name-input-field-${generateUUID()}`).current;

	const addScopeRule = (scopeType: SCOPE_TYPES): void => {
		const newRule: IScopeRuleUIFormat = createDefaultRuleInUIFormat(scopeType);
		setRulesList((currentRulesList) => [...currentRulesList, newRule]);
	};

	const removeScopeRule = (id: string): void => {
		setRulesList((currentRulesList) => [...currentRulesList.filter((rule) => rule.id !== id)]);
	};

	const onRuleChange = (updatedRule: IScopeRuleUIFormat): void => {
		const rulesListCopy = [...rulesList];
		const ruleIndex = rulesListCopy.findIndex((rule) => rule.id === updatedRule.id);

		if (ruleIndex !== -1) {
			rulesListCopy[ruleIndex] = updatedRule;
			setRulesList(rulesListCopy);
		}
	};

	const onSaveSuccess = (): void => {
		propertyViewContext.onViewsChange();
		notificationsContext.toast({
			show: true,
			status: TOAST_STATUS.SUCCESS,
			announcement: 'View saved.'
		});
	};

	const onSaveFailure = (errorMsg: string): void => {
		notificationsContext.toast({
			show: true,
			status: TOAST_STATUS.FAIL,
			announcement: `Failed to save view. ${errorMsg}`
		});
	};

	const addView = async (): Promise<void> => {
		try {
			await saveNewView({ name: viewName, propertyId, rulesList });
			onSaveSuccess();
		} catch (err) {
			onSaveFailure(err.error.message);
			logger.error('Failed to create new view', err);
		}
	};

	const editView = async (): Promise<void> => {
		try {
			await editExistingView({ name: viewName, id: view.id, rulesList, propertyId });
			onSaveSuccess();
		} catch (err) {
			onSaveFailure(err.error.message);
			logger.error('Failed to edit view', err);
		}
	};

	const isInvalidView = useMemo(() => {
		const hasEmptyName = !viewName.trim();
		const hasEmptyRulesList = !rulesList || rulesList?.length === 0;
		const hasEmptyRule = rulesList?.some((rule: IScopeRuleUIFormat) => {
			switch (rule.scopeType) {
				case SCOPE_TYPES.URL:
					return false;
				case SCOPE_TYPES.ISSUE_TYPES:
					return !rule.issueTypes?.length;
				case SCOPE_TYPES.ISSUE_SEVERITIES:
					return isSeverityFilterEnabled && !rule.issueSeverities?.length;
				default:
					// If scopeType is not recognized, consider it invalid
					return true;
			}
		});

		return hasEmptyName || hasEmptyRulesList || hasEmptyRule;
	}, [isSeverityFilterEnabled, rulesList, viewName]);

	const onPopupOpen = async (): Promise<void> => {
		notificationsContext.toast({ show: false });

		if (view) {
			const viewFilters = view.configuration.filters;
			setViewName(view.name);
			setRulesList(viewFilters.length ? buildRulesList(viewFilters) : defaultRulesList?.current);
		}
	};

	const onClosePopup = (onClose?: () => void): void => {
		setViewName('');
		setRulesList(defaultRulesList?.current);
		onClose?.();
	};

	const buttons = [
		{
			title: 'Cancel',
			type: BUTTON_TYPES.GHOST,
			onClick: (onClose: () => void) => onClose()
		},
		{
			title: view ? 'Save' : 'Create',
			disabled: isInvalidView,
			onClick: (onClose: () => void) => {
				if (view) {
					editView();
				} else {
					addView();
				}
				onClose();
			}
		}
	];

	useEffect(() => {
		defaultRulesList.current = createDefaultViewsRulesList({ isSeverityFilterEnabled });
		setRulesList(defaultRulesList.current);
	}, [issueSeveritiesList, issueTypesList, isSeverityFilterEnabled]);

	const title = `${view ? 'Edit' : 'Create'} View`;

	return (
		<SiteScannerPopup
			className="edit-view-button"
			buttons={buttons}
			isCentered
			isBgTransparent
			title={title}
			onOpen={onPopupOpen}
			onClose={onClosePopup}
			triggerButtonProps={{
				accessibilityProps,
				title,
				type,
				children: innerText
			}}
			popupWidth={600}
		>
			<div className="view-popup-header">
				<label id={ARIA_LABEL_ID} className="view-name-label" aria-hidden>
					View name
				</label>
				<EvTextInput
					ariaLabelledBy={ARIA_LABEL_ID}
					ariaLabel="view-name-input"
					className="view-name-input"
					value={viewName}
					onChange={setViewName}
				/>
			</div>
			<EvSection className="view-popup-scope-section">
				<ScopeHeader onActionBtnClick={addScopeRule} actionsList={FILTERED_ACTIONS_LIST} />
				<div className="view-popup-scope-body">
					<ScopeRulesList
						issueTypesList={issueTypesList}
						issueSeveritiesList={issueSeveritiesList}
						rulesList={rulesList}
						onRemove={removeScopeRule}
						onRuleChange={onRuleChange}
					/>
				</div>
			</EvSection>
		</SiteScannerPopup>
	);
};
